import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Loader2,
  Maximize2,
  WifiOff,
  X,
} from "lucide-react";
import { Button } from "./ui/button";
import { OptroImportFullViewDialog } from "./OptroImportFullViewDialog";
import { OptroImportStatusIcon } from "./OptroImportStatusIcon";
import { OptroImportStatusLabel } from "./OptroImportStatusLabel";
import { Progress } from "./ui/progress";
import { cn } from "../lib/utils";
import {
  isFailedImportStatus,
  TrackedOptroImport,
  unimportedSummaryParts,
} from "../lib/optroImportJobs";
import {
  OptroImportItem,
  OptroImportItemStatus,
  OptroImportStage,
} from "../models/audit/optroSync";

const STATUS_CLASS: Record<OptroImportItemStatus, string> = {
  queued: "text-stone-500",
  processing: "text-blue-600",
  complete: "text-emerald-600",
  no_workpaper: "text-amber-600",
  failed: "text-red-600",
  timed_out: "text-red-600",
  cancelled: "text-stone-500",
};

const STATUS_LABEL: Record<
  Exclude<OptroImportItemStatus, "no_workpaper">,
  string
> = {
  queued: "Queued",
  processing: "Processing",
  complete: "Complete",
  failed: "Failed",
  timed_out: "Timed out",
  cancelled: "Cancelled",
};

interface OptroImportProgressCardProps {
  activeJob: Pick<TrackedOptroImport<unknown>, "id" | "job"> | null;
  panelVisible: boolean;
  pollingFailed: boolean;
  onRetry: () => void;
  onDismiss: (id: string) => void;
  // Plural noun for the title and body: "controls", "work steps".
  noun: string;
  // The `no_workpaper` label: "No Workpaper" for controls; work steps have no
  // workpaper concept, so it reads as a plain success there.
  noWorkpaperLabel: string;
  // Controls list how many imported with and without a workpaper once done.
  showWorkpaperBreakdown: boolean;
  // When set, a processing item shows which of the import's tasks it is on
  // under its status.
  stageLabels?: Record<OptroImportStage, string>;
}

// The corner card for one kind of Optro import (controls or work steps).
// Positioned by OptroImportProgressStack, which owns the corner.
export const OptroImportProgressCard = ({
  activeJob,
  panelVisible,
  pollingFailed,
  onRetry,
  onDismiss,
  noun,
  noWorkpaperLabel,
  showWorkpaperBreakdown,
  stageLabels,
}: OptroImportProgressCardProps) => {
  const [expanded, setExpanded] = useState(true);
  const [fullViewOpen, setFullViewOpen] = useState(false);
  if (!activeJob || !panelVisible) return null;
  const job = activeJob.job;

  const finished = job.items.filter(
    (i) => i.status !== "queued" && i.status !== "processing",
  ).length;
  // "Processing X of Y" counts items that have STARTED (processing or done)
  // — a single item mid-import reads "Processing 1 of 1", not "0 of 1". The
  // progress bar still tracks completion via `finished`.
  const started = job.items.filter((i) => i.status !== "queued").length;
  const imported = job.items.filter(
    (i) => i.status === "complete" || i.status === "no_workpaper",
  ).length;
  const withWorkpaper = job.items.filter((i) => i.status === "complete").length;
  const withoutWorkpaper = job.items.filter(
    (i) => i.status === "no_workpaper",
  ).length;
  // Surfaced in the header and summary, not just per-row: failed rows can
  // sit below the fold of the scrollable list, and a green "Import
  // complete" would hide that anything went wrong.
  const failed = job.items.filter((i) => isFailedImportStatus(i.status)).length;
  // Failed, timed out and cancelled counts, split the way Background Tasks
  // splits them: ["1 failed", "1 timed out"].
  const unimportedParts = unimportedSummaryParts(job.items);
  const running = job.items.length - finished;
  const title =
    job.isDone && failed > 0
      ? "Import finished with failures"
      : job.isDone
        ? "Import complete"
        : `Importing ${noun}`;
  const headerIcon =
    job.isDone && failed > 0 ? (
      <AlertTriangle className="h-4 w-4 text-amber-500" />
    ) : job.isDone ? (
      <CheckCircle2 className="h-4 w-4 text-emerald-500" />
    ) : (
      <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
    );
  const summaryParts = [
    running > 0 && `${running} importing`,
    imported > 0 && `${imported} imported`,
  ]
    .filter((part): part is string => typeof part === "string")
    .concat(unimportedParts);

  const renderStatusLabel = (item: OptroImportItem) => (
    <OptroImportStatusLabel
      item={item}
      label={
        item.status === "no_workpaper"
          ? noWorkpaperLabel
          : STATUS_LABEL[item.status]
      }
      className={STATUS_CLASS[item.status]}
    />
  );
  const renderStatus = (item: OptroImportItem) =>
    stageLabels ? (
      <span className="flex flex-col items-end">
        {renderStatusLabel(item)}
        {item.status === "processing" && item.stage && (
          <span className="text-[11px] leading-tight text-stone-400">
            {stageLabels[item.stage]}
          </span>
        )}
      </span>
    ) : (
      renderStatusLabel(item)
    );

  return (
    <>
      {/* The animated spinners make the browser repeatedly promote and demote
          this panel's compositing layer, and each flip re-rasterizes the text
          with a different antialiasing mode (subpixel vs grayscale) — visible
          as the text "shaking" / pulsing in weight while items are processing.
          `will-change-transform` pins the panel to one stable layer, and
          `antialiased` locks the smoothing mode so it can't flip regardless. */}
      <div className="w-full rounded-lg border bg-white shadow-lg will-change-transform antialiased">
        <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
          <button
            type="button"
            onClick={() => setExpanded((value) => !value)}
            className="flex min-w-0 items-center gap-2 text-left text-sm font-medium"
          >
            {headerIcon}
            <span className="truncate">{title}</span>
            <span className="truncate text-xs font-normal text-stone-400">
              {summaryParts.join(" · ")}
            </span>
          </button>
          <div className="flex shrink-0 items-center gap-0.5">
            <button
              type="button"
              onClick={() => setFullViewOpen(true)}
              aria-label="Open full view"
              className="rounded p-1 text-stone-400 hover:bg-stone-100"
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => setExpanded((value) => !value)}
              aria-label={expanded ? "Collapse" : "Expand"}
              className="rounded p-1 text-stone-400 hover:bg-stone-100"
            >
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform duration-200",
                  expanded && "rotate-180",
                )}
              />
            </button>
            <button
              type="button"
              onClick={() => onDismiss(activeJob.id)}
              aria-label="Dismiss"
              className="rounded p-1 text-stone-400 hover:bg-stone-100"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        {expanded && (
          <div className="px-4 py-3">
            <p className="text-sm mb-2">
              {job.isDone
                ? failed > 0
                  ? `Imported ${imported} of ${job.items.length} ${noun} — ${unimportedParts.join(", ")}.`
                  : `Imported ${imported} ${noun}.`
                : `Processing ${started} of ${job.items.length} ${noun}.`}
            </p>
            <Progress
              value={(finished / Math.max(1, job.items.length)) * 100}
              className="h-1.5 mb-2"
            />
            {pollingFailed && !job.isDone && (
              <div className="flex items-center justify-between gap-2 rounded-md bg-red-50 px-2 py-1.5 mb-2 text-xs text-red-700">
                <span className="flex items-center gap-1.5">
                  <WifiOff className="h-3.5 w-3.5" />
                  Lost connection to import status
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-6 px-2 text-xs"
                  onClick={onRetry}
                >
                  Retry
                </Button>
              </div>
            )}
            {showWorkpaperBreakdown && job.isDone && (
              <p className="text-xs text-stone-500 mb-2">
                {withWorkpaper} with a workpaper
                <br />
                {withoutWorkpaper} without a workpaper
                {failed > 0 && (
                  <>
                    <br />
                    <span className="text-red-600">
                      Not imported: {unimportedParts.join(", ")}
                    </span>
                  </>
                )}
              </p>
            )}
            {!showWorkpaperBreakdown && job.isDone && failed > 0 && (
              <p className="text-xs text-red-600 mb-2">
                Not imported: {unimportedParts.join(", ")}
              </p>
            )}
            {/* Scrollbar visuals are hidden (scrolling still works): the
                spinner icons repaint this scroll container every frame, which
                makes the macOS overlay scrollbar flash on and off even when
                the list doesn't overflow — reads as the whole panel
                flickering. */}
            <ul className="space-y-1 max-h-44 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {job.items.map((item) => (
                <li
                  key={item.controlExternalRef}
                  className="flex items-center gap-2 text-xs"
                >
                  <OptroImportStatusIcon status={item.status} />
                  <span className="flex-1 truncate">{item.controlName}</span>
                  {renderStatus(item)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <OptroImportFullViewDialog
        open={fullViewOpen}
        onOpenChange={setFullViewOpen}
        title={title}
        items={job.items}
        renderStatus={renderStatus}
      />
    </>
  );
};
